import { type Dispatch, type ReactNode, type SetStateAction, useEffect, useRef, useState } from "react"
import type { SortingState } from "@tanstack/react-table"

import {
  api,
  type FilaDoPainel,
  type FiltroDaFila,
  type Gravidade,
  type Indicadores,
  type LinhaDaFila,
  type LinhaDoTempo,
} from "../api"
import { Badge } from "../componentes/Badge"
import { BarChartCard, type LinhaDeDados } from "../componentes/BarChartCard"
import { Button } from "../componentes/Button"
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "../componentes/Card"
import { ComDica } from "../componentes/ComDica"
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../componentes/Empty"
import { FiltroAplicado } from "../componentes/FiltroAplicado"
import * as Icones from "../componentes/Icones"
import { Numero, Numeros } from "../componentes/Numeros"
import { FalhaNaSecao } from "../componentes/Secao"
import { Separator } from "../componentes/Separator"
import { Skeleton } from "../componentes/Skeleton"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../componentes/Table"
import { Column, TablePrincipal2 } from "../componentes/TablePrincipal2"
import { Tabs, TabsList, TabsTrigger } from "../componentes/Tabs"
import { estadoLimpo, type EstadoDoPainel } from "../estado-do-painel"
import { inteiro, maiusculaInicial, moedaCurta, plural } from "../formato"
import {
  ABA,
  ATALHO,
  CABECA,
  CONTAGEM_DA_ABA,
  CONTAGEM_DA_ABA_ZERO,
  CORPO_LISTRADO,
  FAZER,
  MAIUSCULA,
  NOTA,
  TEXTO_DO_CADASTRO,
} from "../lib/classes"
import { cn } from "../lib/cn"
import { RECORTES, type RecorteDaLista } from "../recortes"
import type { AbaDaFicha } from "./Ficha"

export type { EstadoDoPainel }

type Props = {
  estado: EstadoDoPainel
  aoMudarEstado: (estado: EstadoDoPainel) => void
  aoAbrirContrato: (id: number, aba?: AbaDaFicha) => void
  aoAbrirLista: (recorte: RecorteDaLista) => void
}

const ABAS_DA_FICHA: readonly AbaDaFicha[] = [
  "contrato",
  "vigencia",
  "processo",
  "execucao",
  "alteracoes",
  "fiscalizacao",
]

function abaDaFicha(aba: string): AbaDaFicha {
  return (ABAS_DA_FICHA as readonly string[]).includes(aba) ? (aba as AbaDaFicha) : "vigencia"
}

const NOME_DA_ABA: Record<AbaDaFicha, string> = {
  contrato: "Contrato",
  vigencia: "Vigência e prazos",
  processo: "Processo",
  execucao: "Execução",
  alteracoes: "Alterações",
  fiscalizacao: "Fiscalização",
}

const MARCAS_NA_CELULA = 3
const LINHAS_DO_ESQUELETO = 5

type Bloco<T> = { dados: T | null; erro: string | null; carregando: boolean; recarregar: () => void }

function mensagemDa(falha: unknown): string {
  return falha instanceof Error ? falha.message : "Falha ao carregar"
}

function useBloco<T>(buscar: () => Promise<T>, chave: string): Bloco<T> {
  const [estado, definirEstado] = useState<{ dados: T | null; erro: string | null; carregando: boolean }>({
    dados: null,
    erro: null,
    carregando: true,
  })
  const [tentativa, definirTentativa] = useState(0)
  const buscarAtual = useRef(buscar)
  buscarAtual.current = buscar

  useEffect(() => {
    let vivo = true
    definirEstado((atual) => ({ ...atual, carregando: true, erro: null }))
    buscarAtual
      .current()
      .then((dados) => {
        if (vivo) definirEstado({ dados, erro: null, carregando: false })
      })
      .catch((falha: unknown) => {
        if (vivo) definirEstado((atual) => ({ ...atual, erro: mensagemDa(falha), carregando: false }))
      })
    return () => {
      vivo = false
    }
  }, [chave, tentativa])

  return { ...estado, recarregar: () => definirTentativa((n) => n + 1) }
}

export function Painel({ estado, aoMudarEstado, aoAbrirContrato, aoAbrirLista }: Props) {
  const [porPagina, definirPorPagina] = useState(10)
  const [abertos, definirAbertos] = useState<Set<number>>(() => new Set())

  const filtro: FiltroDaFila = estado.filtro ?? "todos"
  const faixa = estado.faixa ?? null
  const ordem = estado.ordem ?? null
  const gravidade = estado.gravidade ?? null
  const pagina = estado.pagina ?? 1

  const indicadores = useBloco(() => api.indicadores(), "indicadores")
  const tempo = useBloco(() => api.painelVencimentos(), "vencimentos")
  const fila = useBloco(
    () => api.painelFila({ filtro, faixa, pagina, porPagina, ordem, gravidade }),
    `${filtro}|${faixa}|${pagina}|${porPagina}|${ordem}|${gravidade}`,
  )

  const mudar = (parcial: EstadoDoPainel, manterPagina = false) => {
    definirAbertos(new Set())
    const novo = { ...estado, ...parcial }
    if (!manterPagina) delete novo.pagina
    aoMudarEstado(estadoLimpo(novo))
  }
  const limparFiltros = () => {
    definirAbertos(new Set())
    aoMudarEstado(estadoLimpo({ ordem: estado.ordem }))
  }
  const irParaAFila = () => {
    document.getElementById("fila-de-trabalho")?.scrollIntoView({ block: "start" })
  }

  const [precisamDeAcao, definirPrecisamDeAcao] = useState<number | null>(null)
  useEffect(() => {
    if (fila.dados !== null) definirPrecisamDeAcao(fila.dados.precisam_de_acao)
  }, [fila.dados])
  const filaPendente = precisamDeAcao === null && fila.erro === null

  return (
    <div className="grid gap-3">
      <Resumo
        bloco={indicadores}
        esperando={filaPendente}
        precisamDeAcao={precisamDeAcao}
        aoAbrirLista={aoAbrirLista}
        aoVerAFila={() => {
          limparFiltros()
          irParaAFila()
        }}
      />

      <div className="grid grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-3 max-[1100px]:grid-cols-1">
        <Vencimentos
          bloco={tempo}
          faixaEscolhida={faixa}
          aoEscolher={(chave) => mudar({ faixa: faixa === chave ? undefined : chave, filtro: undefined })}
        />
        <ConfiabilidadeDasDatas
          bloco={tempo}
          aoAbrirLista={aoAbrirLista}
          aoFiltrarSemDataSegura={() => {
            mudar({ filtro: "sem_data_segura", faixa: undefined })
            irParaAFila()
          }}
        />
      </div>

      <Pendencias
        bloco={fila}
        tempo={tempo.dados}
        filtro={filtro}
        faixa={faixa}
        ordem={ordem}
        gravidade={gravidade}
        porPagina={porPagina}
        abertos={abertos}
        aoDefinirAbertos={definirAbertos}
        aoMudar={mudar}
        aoLimparFiltros={limparFiltros}
        aoTrocarPorPagina={(quantos) => {
          definirPorPagina(quantos)
          mudar({})
        }}
        aoAbrirContrato={aoAbrirContrato}
      />
    </div>
  )
}

function Resumo({
  bloco,
  esperando,
  precisamDeAcao,
  aoAbrirLista,
  aoVerAFila,
}: {
  bloco: Bloco<Indicadores>
  esperando: boolean
  precisamDeAcao: number | null
  aoAbrirLista: (recorte: RecorteDaLista) => void
  aoVerAFila: () => void
}) {
  const indicadores = bloco.dados
  let conteudo: ReactNode
  if (bloco.erro !== null && indicadores === null) {
    conteudo = (
      <div className="p-4">
        <FalhaNaSecao mensagem={`Não foi possível carregar o resumo: ${bloco.erro}`} aoTentar={bloco.recarregar} />
      </div>
    )
  } else if (indicadores === null || esperando) {
    conteudo = (
      <div aria-busy="true" className="grid grid-cols-[repeat(auto-fit,minmax(min(10.5rem,100%),1fr))] gap-3 p-4">
        {Array.from({ length: 6 }, (_, indice) => (
          <Skeleton key={indice} className="h-14 w-full" />
        ))}
      </div>
    )
  } else {
    conteudo = (
      <Numeros>
        <Numero rotulo="Com pendência" valor={inteiro(precisamDeAcao)} aoAbrir={aoVerAFila} />
        <Numero
          rotulo="Contratos vigentes"
          valor={inteiro(indicadores.contratos_vigentes)}
          nota={`${moedaCurta(indicadores.valor_vigente)} sob gestão`}
          aoAbrir={() => aoAbrirLista(RECORTES.vigentes(indicadores.contratos_vigentes))}
        />
        <Numero
          rotulo="Vencendo em 90 dias"
          valor={inteiro(indicadores.vencendo_em_90)}
          nota={`${inteiro(indicadores.vencendo_em_30)} em até 30 dias · data do cadastro`}
          aoAbrir={() => aoAbrirLista(RECORTES.vencendoEm(90, indicadores.vencendo_em_90))}
        />
        <Numero
          rotulo="Medições sem ateste"
          valor={inteiro(indicadores.medicoes_sem_ateste)}
          nota={`Em ${plural(indicadores.contratos_com_medicao_sem_ateste ?? 0, "contrato", "contratos")}`}
          aoAbrir={() =>
            aoAbrirLista(RECORTES.comMedicaoSemAteste(indicadores.contratos_com_medicao_sem_ateste))
          }
        />
        <Numero
          rotulo="Sem fiscal designado"
          valor={inteiro(indicadores.contratos_sem_fiscal)}
          nota="Entre os não encerrados"
          aoAbrir={() => aoAbrirLista(RECORTES.semFiscal(indicadores.contratos_sem_fiscal))}
        />
        <Numero
          rotulo="Valor empenhado"
          valor={indicadores.valor_empenhado === null ? "—" : moedaCurta(indicadores.valor_empenhado)}
          nota={indicadores.valor_empenhado === null ? undefined : "Empenhos vigentes, líquidos de anulação"}
          dica={
            indicadores.valor_empenhado !== null
              ? undefined
              : indicadores.empenhos_registrados === 0
                ? "Aparece quando houver empenho registrado."
                : "Não foi possível somar os empenhos."
          }
        />
        <Numero
          rotulo="Valor pago"
          valor={indicadores.valor_pago === null ? "—" : moedaCurta(indicadores.valor_pago)}
          nota={indicadores.valor_pago === null ? undefined : "Pagamentos quitados"}
          dica={
            indicadores.valor_pago !== null
              ? undefined
              : indicadores.pagamentos_registrados === 0
                ? "Aparece quando houver pagamento registrado."
                : "Não foi possível somar os pagamentos."
          }
        />
      </Numeros>
    )
  }

  return (
    <Card className="min-w-0" aria-labelledby="titulo-resumo">
      <CardHeader>
        <CardTitle id="titulo-resumo">Resumo</CardTitle>
      </CardHeader>
      <Separator />
      <CardContent className="px-6">{conteudo}</CardContent>
    </Card>
  )
}

function Vencimentos({
  bloco,
  faixaEscolhida,
  aoEscolher,
}: {
  bloco: Bloco<LinhaDoTempo>
  faixaEscolhida: string | null
  aoEscolher: (chave: string) => void
}) {
  const tempo = bloco.dados
  if (tempo === null) {
    return (
      <Card className="min-w-0" aria-labelledby="titulo-vencimentos">
        <CardHeader>
          <CardTitle id="titulo-vencimentos">Vencimentos</CardTitle>
        </CardHeader>
        <CardContent>
          {bloco.erro !== null ? (
            <FalhaNaSecao mensagem={`Não foi possível carregar os vencimentos: ${bloco.erro}`} aoTentar={bloco.recarregar} />
          ) : (
            <Skeleton aria-busy="true" className="h-56 w-full" />
          )}
        </CardContent>
      </Card>
    )
  }
  const dados: LinhaDeDados[] = tempo.faixas.map((f) => ({
    chave: f.chave,
    rotulo: f.rotulo,
    contratos: f.contratos,
    valorFormatado: f.valor === null ? (f.contratos > 0 ? "Sem valor" : "—") : moedaCurta(f.valor),
    tom: f.tom,
  }))
  return (
    <BarChartCard
      className="min-w-0"
      titulo="Vencimentos"
      descricao={
        <span className={NOTA}>
          {inteiro(tempo.com_data)} com data de término
          {tempo.sem_data > 0 && ` · ${inteiro(tempo.sem_data)} sem data`}
        </span>
      }
      dados={dados}
      chaveSelecionada={faixaEscolhida}
      aoSelecionar={aoEscolher}
      tituloDoClique={(item, selecionada) =>
        selecionada
          ? "Tirar o recorte da fila"
          : `Mostrar na fila ${plural(item.contratos, "contrato", "contratos")} desta faixa`
      }
    />
  )
}

const TOM_DO_NIVEL: Record<string, string> = {
  A: "bg-(--azul-800)",
  B: "bg-(--azul-600)",
  C: "bg-(--azul-400)",
  D: "bg-(--azul-200)",
  E: "bg-(--status-atencao)",
}
const SEM_NIVEL = "bg-muted"

function ConfiabilidadeDasDatas({
  bloco,
  aoAbrirLista,
  aoFiltrarSemDataSegura,
}: {
  bloco: Bloco<LinhaDoTempo>
  aoAbrirLista: (recorte: RecorteDaLista) => void
  aoFiltrarSemDataSegura: () => void
}) {
  const tempo = bloco.dados
  return (
    <Card className="flex h-full min-w-0 flex-col" aria-labelledby="titulo-confiabilidade">
      <CardHeader>
        <CardTitle id="titulo-confiabilidade">Confiabilidade das datas</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col justify-between px-4 py-3">
        {tempo === null ? (
          bloco.erro !== null ? (
            <FalhaNaSecao mensagem={`Não foi possível carregar as datas: ${bloco.erro}`} aoTentar={bloco.recarregar} />
          ) : (
            <div aria-busy="true" className="flex flex-col gap-2">
              <Skeleton className="h-3.5 w-full" />
              {[0, 1, 2, 3].map((linha) => (
                <Skeleton key={linha} className="h-5 w-3/4" />
              ))}
            </div>
          )
        ) : (
          <>
            <div>
              <div className="flex h-3.5 overflow-hidden rounded-full border border-border bg-muted" aria-hidden="true">
                {tempo.niveis.map((n) => (
                  <span
                    key={n.nivel}
                    className={cn("block h-full", TOM_DO_NIVEL[n.nivel] ?? SEM_NIVEL)}
                    style={{ width: `${(n.contratos / Math.max(1, tempo.ativos)) * 100}%` }}
                  />
                ))}
              </div>
              <ul className="m-0 mt-3 flex list-none flex-col gap-1.5 p-0">
                {tempo.niveis.map((n) => (
                  <li key={n.nivel}>
                    <button
                      type="button"
                      className="group grid w-full cursor-pointer grid-cols-[10px_minmax(0,1fr)_auto] items-center gap-2 border-0 bg-transparent py-1 text-left text-xs text-foreground hover:text-primary"
                      onClick={() => aoAbrirLista(RECORTES.porNivel(n.nivel, n.contratos, n.rotulo))}
                    >
                      <span className={cn("size-2.5 rounded-[2px]", TOM_DO_NIVEL[n.nivel] ?? SEM_NIVEL)} aria-hidden="true" />
                      <span className={cn("group-hover:underline", MAIUSCULA)}>{n.rotulo}</span>
                      <span className="font-semibold text-muted-foreground tabular-nums group-hover:text-primary">
                        {inteiro(n.contratos)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-4 flex flex-col items-start gap-1 border-t border-border pt-3">
              <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs" onClick={aoFiltrarSemDataSegura}>
                <Icones.Ressalva tamanho={13} />
                {inteiro(tempo.sem_definicao_segura)} sem data segura — ver na fila
              </Button>
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs"
                onClick={() => aoAbrirLista(RECORTES.semProvidencia(tempo.sem_providencia_de_continuidade))}
              >
                <Icones.Providencia tamanho={13} />
                {inteiro(tempo.sem_providencia_de_continuidade)} a vencer sem providência concluída
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

const ICONE_DA_MARCA: Record<string, Icones.Icone> = {
  vencido: Icones.Vencido,
  data_provavel: Icones.Vencendo,
  clausula_nao_lida: Icones.Ressalva,
  sem_prorrogacao: Icones.Ressalva,
  prorrogavel: Icones.Reabrir,
  sem_fiscal: Icones.Pessoa,
  providencias: Icones.Atrasada,
  sem_data_segura: Icones.Pendente,
  garantia: Icones.Chave,
  acrescimos: Icones.Mais,
  medicao: Icones.Pendente,
  datas_em_conflito: Icones.Contestar,
}

const TOM_DA_GRAVIDADE = { critico: "vermelho", atencao: "ambar", informacao: "cinza" } as const

const PONTO_DA_GRAVIDADE: Record<Gravidade, string> = {
  critico: "bg-(--vermelho)",
  atencao: "bg-status-atencao",
  informacao: "bg-muted-foreground",
}

const NENHUM_DA_GRAVIDADE: Record<Gravidade, string> = {
  critico: "Nenhum contrato crítico",
  atencao: "Nenhum contrato de atenção",
  informacao: "Nenhum contrato de informação",
}

const NOME_DA_GRAVIDADE: Record<Gravidade, string> = {
  critico: "Crítico",
  atencao: "Atenção",
  informacao: "Informação",
}

type LinhaNaTabela = LinhaDaFila & { esqueleto?: boolean }

const ESQUELETO: LinhaNaTabela[] = Array.from({ length: LINHAS_DO_ESQUELETO }, (_, indice) => ({
  contrato_id: -(indice + 1),
  numero: "",
  objeto: null,
  contratado: null,
  situacao: "",
  gravidade: "informacao",
  dias: null,
  fim: null,
  data_provavel: false,
  prazo: "",
  aba: "vigencia",
  marcas: [],
  providencias_atrasadas: 0,
  providencias: [],
  problemas: [],
  esqueleto: true,
}))

function Pendencias({
  bloco,
  tempo,
  filtro,
  faixa,
  ordem,
  gravidade,
  porPagina,
  abertos,
  aoDefinirAbertos,
  aoMudar,
  aoLimparFiltros,
  aoTrocarPorPagina,
  aoAbrirContrato,
}: {
  bloco: Bloco<FilaDoPainel>
  tempo: LinhaDoTempo | null
  filtro: FiltroDaFila
  faixa: string | null
  ordem: EstadoDoPainel["ordem"] | null
  gravidade: Gravidade | null
  porPagina: number
  abertos: Set<number>
  aoDefinirAbertos: Dispatch<SetStateAction<Set<number>>>
  aoMudar: (parcial: EstadoDoPainel, manterPagina?: boolean) => void
  aoLimparFiltros: () => void
  aoTrocarPorPagina: (porPagina: number) => void
  aoAbrirContrato: (id: number, aba?: AbaDaFicha) => void
}) {
  const [ultima, definirUltima] = useState<FilaDoPainel | null>(null)
  useEffect(() => {
    if (bloco.dados !== null) definirUltima(bloco.dados)
  }, [bloco.dados])
  const fila = bloco.dados ?? ultima

  const alternar = (id: number) => {
    aoDefinirAbertos((atuais) => {
      const novos = new Set(atuais)
      if (novos.has(id)) novos.delete(id)
      else novos.add(id)
      return novos
    })
  }

  const filtros: ReactNode[] = []
  const rotuloDoFiltro = fila?.filtros.find((f) => f.chave === filtro)?.rotulo ?? filtro
  const rotuloDaFaixa = tempo?.faixas.find((f) => f.chave === faixa)?.rotulo ?? faixa
  if (filtro !== "todos") {
    filtros.push(
      <FiltroAplicado key="filtro" compacto rotulo="Filtro" valor={rotuloDoFiltro} aoLimpar={() => aoMudar({ filtro: undefined })} />,
    )
  }
  if (faixa !== null) {
    filtros.push(
      <FiltroAplicado
        key="faixa"
        compacto
        rotulo="Faixa"
        valor={maiusculaInicial(rotuloDaFaixa ?? "")}
        aoLimpar={() => aoMudar({ faixa: undefined })}
      />,
    )
  }
  if (gravidade !== null) {
    filtros.push(
      <FiltroAplicado
        key="gravidade"
        compacto
        rotulo="Gravidade"
        valor={NOME_DA_GRAVIDADE[gravidade]}
        aoLimpar={() => aoMudar({ gravidade: undefined })}
      />,
    )
  }
  const comFiltro = filtros.length > 0
  if (filtros.length > 1) {
    filtros.push(
      <Button key="limpar" type="button" variant="ghost" size="sm" className="h-7 text-xs text-vermelho hover:text-vermelho" onClick={aoLimparFiltros}>
        <Icones.Fechar />
        Limpar todos os filtros
      </Button>,
    )
  }

  const carregando = bloco.carregando || fila === null
  const linhas = carregando ? ESQUELETO : (fila.dados as LinhaNaTabela[])
  const criticos = carregando ? [] : fila.dados.filter((l) => l.gravidade === "critico")

  const acoes = (
    <div data-slot="acoes-da-fila" className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:flex-wrap md:items-center">
      {fila !== null && (
        <div role="group" aria-label="Gravidade" className="grid grid-cols-2 gap-2 md:inline-flex md:flex-wrap md:items-center md:gap-1">
          {fila.gravidades.map((g) => {
            const escolhida = g.chave === gravidade
            const vazia = g.contratos === 0 && !escolhida
            return (
              <Button
                key={g.chave}
                type="button"
                variant={escolhida ? "default" : "outline"}
                size="sm"
                aria-pressed={escolhida}
                disabled={vazia}
                className={cn(g.chave === "informacao" && "col-span-2")}
                title={vazia ? NENHUM_DA_GRAVIDADE[g.chave] : undefined}
                onClick={() => aoMudar({ gravidade: escolhida ? undefined : g.chave })}
              >
                <span aria-hidden="true" className={cn("size-2 rounded-full", PONTO_DA_GRAVIDADE[g.chave])} />
                {g.rotulo}
                <span className="font-semibold tabular-nums">{inteiro(g.contratos)}</span>
              </Button>
            )
          })}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 md:flex md:items-center">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={criticos.length === 0}
          className="text-xs"
          title={criticos.length === 0 ? "Nenhum contrato crítico nesta página" : undefined}
          onClick={() => aoDefinirAbertos((atuais) => new Set([...atuais, ...criticos.map((l) => l.contrato_id)]))}
        >
          <Icones.Aumentar />
          Abrir os críticos
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={abertos.size === 0}
          className="text-xs"
          title={abertos.size === 0 ? "Nenhum contrato aberto" : undefined}
          onClick={() => aoDefinirAbertos(new Set())}
        >
          <Icones.Diminuir />
          Recolher todos
        </Button>
      </div>
    </div>
  )

  const vazio = comFiltro ? (
    <Empty className="py-8">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Icones.Buscar />
        </EmptyMedia>
        <EmptyTitle>Nada encontrado</EmptyTitle>
        <EmptyDescription>Nenhum contrato com pendência casa com estes filtros.</EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <Button type="button" variant="outline" size="sm" onClick={aoLimparFiltros}>
          <Icones.Fechar />
          Limpar todos os filtros
        </Button>
      </EmptyContent>
    </Empty>
  ) : (
    <Empty className="py-8">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Icones.Concluida />
        </EmptyMedia>
        <EmptyTitle>Nenhum contrato precisa de ação agora</EmptyTitle>
      </EmptyHeader>
    </Empty>
  )

  const ordenacao: SortingState = ordem === null || ordem === undefined ? [] : [{ id: "numero", desc: ordem === "-contrato" }]

  return (
    <Card className="min-w-0" id="fila-de-trabalho" aria-labelledby="titulo-pendencias">
      <CardHeader>
        <CardTitle id="titulo-pendencias">Pendências</CardTitle>
        {fila !== null && (
          <CardAction className="text-xs text-muted-foreground tabular-nums">
            <span className="font-medium text-status-erro">{plural(fila.por_gravidade.critico, "crítico", "críticos")}</span>
            {" · "}
            <span className="font-medium text-status-atencao">{inteiro(fila.por_gravidade.atencao)} de atenção</span>
            {fila.so_designados && " · Só os contratos em que você é designado"}
          </CardAction>
        )}
      </CardHeader>
      <CardContent>
        {fila === null && bloco.erro !== null ? (
          <FalhaNaSecao mensagem={`Não foi possível carregar as pendências: ${bloco.erro}`} aoTentar={bloco.recarregar} />
        ) : (
          <>
            {bloco.erro !== null && (
              <div className="pb-3">
                <FalhaNaSecao mensagem={`Não foi possível atualizar as pendências: ${bloco.erro}`} aoTentar={bloco.recarregar} />
              </div>
            )}
            <TablePrincipal2<LinhaNaTabela>
              data={linhas}
              listrada
              pageCount={Math.max(1, Math.ceil((fila?.meta.total ?? 0) / porPagina))}
              currentPage={fila?.meta.pagina ?? 1}
              pageSize={porPagina}
              totalRegistros={fila?.meta.total ?? 0}
              onPageChange={(novaPagina) => aoMudar({ pagina: novaPagina }, true)}
              onPageSizeChange={aoTrocarPorPagina}
              ordenacaoNoServidor
              ordenacao={ordenacao}
              onSortingChange={(nova) => {
                const primeira = nova[0]
                aoMudar({ ordem: primeira === undefined ? undefined : primeira.desc ? "-contrato" : "contrato" })
              }}
              abasNaBarra={
                fila !== null && (
                  <Tabs value={filtro} onValueChange={(valor) => aoMudar({ filtro: valor as FiltroDaFila })}>
                    <TabsList aria-label="Filtros das pendências" className="h-auto max-w-full flex-wrap justify-start">
                      {fila.filtros.map((f) => (
                        <TabsTrigger key={f.chave} value={f.chave} className={ABA}>
                          {f.rotulo}
                          <span
                            data-contagem={f.contratos === 0 ? "zero" : "cheia"}
                            className={cn(CONTAGEM_DA_ABA, f.contratos === 0 && CONTAGEM_DA_ABA_ZERO)}
                          >
                            {inteiro(f.contratos)}
                          </span>
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </Tabs>
                )
              }
              temAbas={
                comFiltro ? (
                  <div data-slot="filtros-da-fila" className="flex flex-wrap items-center gap-2">
                    {filtros}
                  </div>
                ) : undefined
              }
              acaoNoTopo={acoes}
              mensagemVazia={vazio}
              aoClicarNaLinha={(linha) => alternar(linha.contrato_id)}
              linhaClicavel={(linha) => linha.esqueleto !== true}
              linhaAberta={(linha) => abertos.has(linha.contrato_id)}
              detalheDaLinha={(linha) => (
                <DetalheDaLinha linha={linha} aba={abaDaFicha(linha.aba)} aoAbrirContrato={aoAbrirContrato} />
              )}
            >
              <Column<LinhaNaTabela>
                name="numero"
                label="Contrato"
                classe="md:w-[12%]"
                enableSorting
                render={(_valor, linha) =>
                  linha.esqueleto === true ? (
                    <Skeleton data-slot="esqueleto-da-fila" className="h-5 w-24" />
                  ) : (
                    <CelulaDoContrato
                      linha={linha}
                      aberta={abertos.has(linha.contrato_id)}
                      aoAlternar={() => alternar(linha.contrato_id)}
                      aoAbrirContrato={aoAbrirContrato}
                    />
                  )
                }
              />
              <Column<LinhaNaTabela>
                name="objeto"
                label="Objeto e contratado"
                enableSorting={false}
                render={(_valor, linha) =>
                  linha.esqueleto === true ? <Skeleton className="h-5 w-full" /> : <CelulaDoObjeto linha={linha} />
                }
              />
              <Column<LinhaNaTabela>
                name="prazo"
                label="Prazo mais urgente"
                classe="md:w-[16%]"
                enableSorting={false}
                render={(_valor, linha) =>
                  linha.esqueleto === true ? (
                    <Skeleton className="h-5 w-28" />
                  ) : (
                    <span className={cn("block font-medium text-foreground tabular-nums", MAIUSCULA)}>{linha.prazo}</span>
                  )
                }
              />
              <Column<LinhaNaTabela>
                name="marcas"
                label="Problemas"
                classe="md:w-[30%]"
                enableSorting={false}
                render={(_valor, linha) =>
                  linha.esqueleto === true ? <Skeleton className="h-6 w-40" /> : <MarcasDaLinha linha={linha} />
                }
              />
            </TablePrincipal2>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function CelulaDoContrato({
  linha,
  aberta,
  aoAlternar,
  aoAbrirContrato,
}: {
  linha: LinhaDaFila
  aberta: boolean
  aoAlternar: () => void
  aoAbrirContrato: (id: number, aba?: AbaDaFicha) => void
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-start gap-1.5">
        <button
          type="button"
          className="inline-flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent p-0 text-muted-foreground hover:bg-accent hover:text-primary focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring aria-expanded:text-primary [&_svg]:transition-transform [&_svg]:duration-200 aria-expanded:[&_svg]:rotate-90"
          onClick={aoAlternar}
          aria-expanded={aberta}
          aria-label={aberta ? `Fechar os detalhes do contrato ${linha.numero}` : `Mostrar os problemas do contrato ${linha.numero}`}
          title={aberta ? "Fecha os detalhes deste contrato" : "Mostra os problemas deste contrato e o que fazer em cada um"}
        >
          <Icones.Proxima />
        </button>
        <button
          type="button"
          className={cn(ATALHO, "min-w-0 font-semibold break-words tabular-nums")}
          onClick={() => aoAbrirContrato(linha.contrato_id, abaDaFicha(linha.aba))}
        >
          {linha.numero}
        </button>
      </div>
      <span
        className={cn(
          "block pl-6 text-[0.6875rem] first-letter:uppercase",
          linha.gravidade === "critico" && "font-semibold text-(--vermelho)",
          linha.gravidade === "atencao" && "text-status-atencao",
          linha.gravidade === "informacao" && "text-muted-foreground",
        )}
      >
        {NOME_DA_GRAVIDADE[linha.gravidade]}
      </span>
    </div>
  )
}

function CelulaDoObjeto({ linha }: { linha: LinhaDaFila }) {
  return (
    <div data-slot="objeto-da-linha" className="min-w-0">
      <span className="block w-0 min-w-full truncate font-medium text-foreground">{linha.objeto ?? "—"}</span>
      <span className="mt-0.5 block w-0 min-w-full truncate text-[0.6875rem] text-muted-foreground">
        Contratado: {linha.contratado ?? "—"}
      </span>
    </div>
  )
}

function MarcasDaLinha({ linha }: { linha: LinhaDaFila }) {
  if (linha.marcas.length === 0) {
    return <span className="text-xs text-muted-foreground italic">Nenhuma pendência apontada</span>
  }
  const visiveis = linha.marcas.slice(0, MARCAS_NA_CELULA)
  const demais = linha.marcas.slice(MARCAS_NA_CELULA)
  return (
    <div className="flex flex-wrap gap-1.5">
      {visiveis.map((marca) => {
        const Icone = ICONE_DA_MARCA[marca.chave] ?? Icones.Etiqueta
        return (
          <Badge key={marca.chave} data-marca={marca.chave} tom={TOM_DA_GRAVIDADE[marca.gravidade]} className="px-2 py-1 text-[0.6875rem]">
            <Icone tamanho={12} />
            {maiusculaInicial(marca.rotulo)}
          </Badge>
        )
      })}
      {demais.length > 0 && (
        <ComDica dica={demais.map((marca) => maiusculaInicial(marca.rotulo)).join(", ")} semSublinhado>
          <Badge variant="secondary" className="px-2 py-1 text-[0.6875rem]">
            +{demais.length}
          </Badge>
        </ComDica>
      )}
    </div>
  )
}

function DetalheDaLinha({
  linha,
  aba,
  aoAbrirContrato,
}: {
  linha: LinhaDaFila
  aba: AbaDaFicha
  aoAbrirContrato: (id: number, aba?: AbaDaFicha) => void
}) {
  const outros = linha.problemas.filter((p) => p.tipo !== "providencia_atrasada")
  const primeiraProvidencia = linha.problemas.find((p) => p.tipo === "providencia_atrasada")

  return (
    <div className="grid justify-items-stretch gap-3 whitespace-normal">
      <ul className="m-0 block w-full list-none divide-y divide-border overflow-hidden rounded-md border border-border bg-card p-0 [&>li]:p-3">
        {linha.objeto !== null && (
          <li>
            <span className="mb-1 block text-xs font-semibold tracking-[0.04em] text-primary uppercase">
              Objeto completo
            </span>
            <p className={cn("m-0 text-xs text-foreground", TEXTO_DO_CADASTRO)}>{linha.objeto}</p>
          </li>
        )}

        {outros.length === 0 && primeiraProvidencia === undefined && (
          <li>
            <p className="m-0 text-xs text-muted-foreground">Sem pendência. Aparece aqui pela faixa escolhida.</p>
          </li>
        )}
        {outros.map((problema, indice) => (
          <li key={`${problema.tipo}-${indice}`}>
            <strong className="flex items-center gap-2 text-xs text-foreground">
              <span className="inline-block size-2 shrink-0 rounded-full bg-status-atencao" aria-hidden="true" />
              {problema.titulo}
            </strong>
            <p className={FAZER}>{problema.o_que_fazer}</p>
            {(problema.procedencia !== null || problema.fundamento !== null) && (
              <Fundamento procedencia={problema.procedencia} fundamento={problema.fundamento} />
            )}
          </li>
        ))}

        {primeiraProvidencia !== undefined && (
          <li>
            <strong className="flex items-center gap-2 text-xs text-foreground">
              <span className="inline-block size-2 shrink-0 rounded-full bg-status-erro" aria-hidden="true" />
              {plural(linha.providencias_atrasadas, "providência de continuidade atrasada", "providências de continuidade atrasadas")}
            </strong>
            <p className={FAZER}>{primeiraProvidencia.o_que_fazer}</p>

            <Subgrupo rotulo="Ver providências uma a uma" semRespiro>
              <Table className="border-collapse bg-card text-xs leading-(--linha-densa) [&_td]:border-b [&_td]:border-border [&_td]:px-3 [&_td]:py-1.5">
                <TableHeader>
                  <TableRow>
                    <TableHead className={cn(CABECA, "text-center")}>Faixa</TableHead>
                    <TableHead className={CABECA}>Providência</TableHead>
                    <TableHead className={cn(CABECA, "text-center")}>Atraso</TableHead>
                    <TableHead className={cn(CABECA, "text-center")}>Responsável</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className={CORPO_LISTRADO}>
                  {linha.providencias.map((p, indice) => (
                    <TableRow key={`${p.faixa_dias}-${indice}`}>
                      <TableCell className="text-center tabular-nums">{p.faixa_dias === null ? "—" : `${p.faixa_dias} dias`}</TableCell>
                      <TableCell className={MAIUSCULA}>{p.descricao ?? "—"}</TableCell>
                      <TableCell className="text-center font-semibold text-status-erro tabular-nums">
                        {p.atraso_dias === null ? "—" : `${p.atraso_dias} dias`}
                      </TableCell>
                      <TableCell className={cn("text-center", MAIUSCULA)}>{p.responsavel ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Subgrupo>
          </li>
        )}
      </ul>

      <div className="flex justify-end">
        <Button size="sm" type="button" onClick={() => aoAbrirContrato(linha.contrato_id, aba)}>
          <Icones.Abrir />
          Abrir a ficha em {NOME_DA_ABA[aba]}
        </Button>
      </div>
    </div>
  )
}

function Subgrupo({
  rotulo,
  semRespiro = false,
  children,
}: {
  rotulo: string
  semRespiro?: boolean
  children: ReactNode
}) {
  return (
    <details className={cn("group mt-2 rounded-md border border-border", semRespiro && "overflow-hidden")}>
      <summary className="flex cursor-pointer list-none items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent hover:text-primary focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring group-open:rounded-b-none group-open:border-b group-open:border-border group-open:bg-accent group-open:text-primary [&::-webkit-details-marker]:hidden [&_svg]:shrink-0 [&_svg]:text-muted-foreground [&_svg]:transition-transform [&_svg]:duration-200 group-open:[&_svg]:rotate-90 group-open:[&_svg]:text-primary">
        <Icones.Proxima />
        {rotulo}
      </summary>
      <div className={semRespiro ? undefined : "p-3"}>{children}</div>
    </details>
  )
}

function Fundamento({ procedencia, fundamento }: { procedencia: string | null; fundamento: string | null }) {
  return (
    <Subgrupo rotulo="Fundamento">
      <p className="m-0 text-xs leading-(--linha-densa) text-muted-foreground">
        {procedencia !== null && <>Limite {procedencia}. </>}
        {fundamento}
      </p>
    </Subgrupo>
  )
}
